import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
} from "antd";
import {  CheckCircleOutlined } from "@ant-design/icons";
import { EyeOutlined, PlayCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import Service from "../../service";
import EmpNameCodeCell from "../common/EmpNameCodeCell";
import { useDispatch } from "react-redux";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions/Auth";
import {
  APPROVAL_ACTIONS,
  expenseApi,
  formatMoney,
  statusTag,
} from "./expenseHelpers";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";
import ApprovalStatusCell from "../Approvals/ApprovalStatusCell";
import { getResource } from "../../util/getResourcePermission";
import config from "../../permission/config.json";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import "./expenseManagement.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";
import { QuestionCircleOutlined } from "@ant-design/icons";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";
import BulkImportMenu from "../common/BulkImport/BulkImportMenu";

const { TextArea } = Input;
const { Option } = Select;

const TOUR_STORAGE_KEY = "tour_seen_expense_approval";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_expense_approval";

// Approval screen - approval-queue table for RM / Account Team with per-row
// actions (Approve / Reject / RequestChanges / Hold / Comment) and an
// approvalHistory timeline in the detail modal.
const ExpenseApproval = () => {
  const dateFormat = useCompanyDateFormat();
  const dispatch = useDispatch();
  const [actionForm] = Form.useForm();

  const [queue, setQueue] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
  const [total, setTotal] = useState(0);

  const [actionModal, setActionModal] = useState({ open: false, record: null });
  const [detail, setDetail] = useState({ open: false, data: null });

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isTourHintOpen, setIsTourHintOpen] = useState(false);

  const allowed = useMemo(
    () => getResource(config.RESOURCES.EXPENSE_APPROVAL, ["update"]),
    []
  );

  useEffect(() => {
    if (!hasTourFlag(TOUR_STORAGE_KEY) && !hasTourFlag(TOUR_HINT_STORAGE_KEY)) {
      setIsTourHintOpen(true);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expenseApprovalQueue, {
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setQueue(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      message.error(err.message || "Failed to load approval queue");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const openAction = (record) => {
    actionForm.resetFields();
    setActionModal({ open: true, record });
  };

  const submitAction = async (values) => {
    const { record } = actionModal;
    dispatch(showAuthLoader());
    try {
      await expenseApi.put(`${Service.expense}/${record._id}/action`, {
        action: values.action,
        comment: values.comment || "",
      });
      message.success(`Action '${values.action}' applied`);
      setActionModal({ open: false, record: null });
      loadQueue();
    } catch (err) {
      message.error(err.message || "Failed to apply action");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const openDetail = async (record) => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(`${Service.expense}/${record._id}`);
      setDetail({ open: true, data });
    } catch (err) {
      message.error(err.message || "Failed to load detail");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const tourSteps = useMemo(() => {
    const steps = [
      {
        target: '[data-tour="ea-table"]',
        title: "Approval Queue",
        content:
          "Every expense waiting on your review lands here, newest first. Use View or Action per row - nothing here needs manual refresh.",
      },
      {
        target: '[data-tour="ea-status"]',
        title: "Status",
        content:
          "Pending, Approved, Rejected or On Hold. When a request needs more than one approver, Pending also shows a Step X of Y note so you know where it is stuck.",
      },
      {
        target: '[data-tour="ea-exception"]',
        title: "Exception",
        content:
          "A red Yes flags requests that broke a policy rule (over limit, missing bill, duplicate, etc). Review these more carefully before acting.",
      },
      {
        target: '[data-tour="ea-actions"]',
        title: "View and Action",
        content: allowed
          ? "View opens the full detail - amount, bill, vendor and the complete approval history timeline. Action opens Approve, Reject, Hold or Request Changes - a comment is required when you reject or request changes."
          : "View opens the full detail - amount, bill, vendor and the complete approval history timeline.",
      },
    ];
    return steps;
  }, [allowed]);

  const hintSteps = useMemo(
    () => [
      {
        target: '[data-tour="ea-guide-icon"]',
        title: "New here?",
        content: "Click here for a quick guide to the approval queue.",
        shape: "circle",
      },
    ],
    []
  );

  const columns = [
    {
      title: "Advance ID",
      dataIndex: "advanceCode",
      key: "advanceCode",
      render: (v) => v || "-",
    },
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (text, record) => <EmpNameCodeCell name={record.employeeName} code={record.empCode} />,
    },
    {
      title: "Date",
      dataIndex: "expenseDate",
      key: "expenseDate",
      render: (v) => (v ? moment(v).format(dateFormat) : "-"),
    },
    { title: "Category", dataIndex: "category", key: "category", render: (v) => v || "-" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => formatMoney(v),
    },
    { title: "Vendor", dataIndex: "vendorName", key: "vendorName", render: (v) => v || "-" },
    { title: "Bill No.", dataIndex: "billNumber", key: "billNumber", render: (v) => v || "-" },
    {
      title: <span data-tour="ea-status">Status</span>,
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      render: (v, record) => {
        // Engine-enabled companies carry approval_progress - same shared
        // status cell every other onboarded module uses, click opens the full
        // per-level chain breakdown. Legacy companies (field absent) keep the
        // existing plain tag + "Step X of Y" hint, unchanged.
        if (record.approval_progress) {
          return <ApprovalStatusCell progress={record.approval_progress} />;
        }
        const progress = record.approvalProgress;
        // A multi-step workflow keeps the status on "Pending" until the last
        // step, so show which step it is waiting at - otherwise approving looks
        // like it did nothing.
        const showStage = v === "Pending" && progress?.totalSteps > 1;
        return (
          <>
            {statusTag(v)}
            {showStage && (
              <div className="expense-muted expense-status-stage">
                {`Step ${Math.min(progress.approvedCount + 1, progress.totalSteps)} of ${progress.totalSteps}`}
                {progress.currentStepRole ? ` · ${progress.currentStepRole}` : ""}
              </div>
            )}
          </>
        );
      },
    },
    {
      title: <span data-tour="ea-exception">Exception</span>,
      dataIndex: "isException",
      key: "isException",
      render: (v) => (v ? <Tag color="red">Yes</Tag> : "-"),
    },
    {
      title: <span data-tour="ea-actions">Actions</span>,
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              size="small"
              className="expense-approval-icon-btn"
              icon={<EyeOutlined />}
              onClick={() => openDetail(record)}
            />
          </Tooltip>
          {/* canAct is set for Sub-Admin only: false outside its team's RM/ARM step */}
          {allowed && record?.canAct !== false && (
            <Tooltip title="Action">
              <Button
                size="small"
                className="expense-approval-icon-btn expense-approval-icon-btn-primary"
                icon={<CheckCircleOutlined />}
                onClick={() => openAction(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="employee-card expense-page">
      <div className="inout-header text-left">
        <h2>
          Expense Approval Queue{" "}
          <QuestionCircleOutlined
            data-tour="ea-guide-icon"
            className="guide-title-icon"
            role="button"
            tabIndex={0}
            onClick={() => setIsTourOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setIsTourOpen(true);
            }}
            title="Click for a step-by-step guide"
          />
        </h2>
        <div className="header-btns">
          {allowed && (
            <BulkImportMenu
              moduleLabel="Expense"
              endpoints={{
                upload: Service.expenseImportUpload,
                status: Service.expenseImportStatus,
                history: Service.expenseImportHistory,
                downloadErrors: Service.expenseImportDownloadErrors,
                undo: Service.expenseImportUndo,
                cancel: Service.expenseImportCancel,
                template: Service.expenseImportTemplate,
              }}
              sampleFileName="expense_import_sample_template.xlsx"
              onImportComplete={loadQueue}
            />
          )}
        </div>
      </div>
      <Card className="main-content-wrapper">
        <div className="main-table-wrapper" data-tour="ea-table">
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={queue}
            locale={{ emptyText: <NodataFoundIcon /> }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (current, pageSize) => setPagination({ current, pageSize }),
            }}
            scroll={{ x: true }}
          />
        </div>
      </Card>

      <Modal
        title="Take Action"
        open={actionModal.open}
        onCancel={() => setActionModal({ open: false, record: null })}
        onOk={() => actionForm.submit()}
        okText="Apply"
        destroyOnClose
      >
        <Form form={actionForm} layout="vertical" onFinish={submitAction}>
          <Form.Item
            name="action"
            label="Action"
            rules={[{ required: true, message: "Select an action" }]}
          >
            <Select placeholder="Select action">
              {APPROVAL_ACTIONS.map((a) => (
                <Option key={a} value={a}>
                  {a.replace(/([a-z])([A-Z])/g, "$1 $2")}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.action !== cur.action}
          >
            {({ getFieldValue }) => {
              const act = getFieldValue("action");
              const mustComment = act === "Reject" || act === "RequestChanges";
              return (
                <Form.Item
                  name="comment"
                  label="Comment"
                  rules={
                    mustComment
                      ? [
                          {
                            required: true,
                            whitespace: true,
                            message:
                              "A comment is required when rejecting or requesting changes.",
                          },
                        ]
                      : []
                  }
                >
                  <TextArea
                    rows={3}
                    placeholder={
                      mustComment
                        ? "Explain the reason (required)"
                        : "Optional comment"
                    }
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Expense Detail"
        width={640}
        className="expense-detail-modal"
        open={detail.open}
        onCancel={() => setDetail({ open: false, data: null })}
        footer={[
          <Button key="close" onClick={() => setDetail({ open: false, data: null })}>
            Close
          </Button>,
        ]}
        destroyOnClose
      >
        {detail.data && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Category">{detail.data.category || "-"}</Descriptions.Item>
              <Descriptions.Item label="Amount">{formatMoney(detail.data.amount)}</Descriptions.Item>
              <Descriptions.Item label="Vendor">{detail.data.vendorName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Bill No.">{detail.data.billNumber || "-"}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {detail.data.expenseDate
                  ? moment(detail.data.expenseDate).format(dateFormat)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {statusTag(detail.data.approvalStatus)}
              </Descriptions.Item>
              <Descriptions.Item label="Exception">
                {detail.data.isException ? <Tag color="red">Yes</Tag> : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {detail.data.description || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Attachments">
                {(detail.data.attachments || []).length ? (
                  <Space direction="vertical" size={2}>
                    {detail.data.attachments.map((file, i) => (
                      <a
                        key={i}
                        href={`${Service.Server_Base_URL}/uploads/expenses/${file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {detail.data.attachments.length > 1
                          ? `View bill ${i + 1}`
                          : "View bill"}
                      </a>
                    ))}
                  </Space>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
            </Descriptions>

            <h4 className="expense-section-title">Approval History</h4>
            {(detail.data.approvalHistory || []).length ? (
              <Timeline>
                {detail.data.approvalHistory.map((entry, idx) => (
                  <Timeline.Item key={idx}>
                    <strong>{entry.action}</strong> by {entry.roleName || "-"}
                    <div className="expense-muted">
                      {entry.at ? moment(entry.at).format(`${dateFormat} HH:mm`) : ""}
                    </div>
                    {entry.comment && <div>{entry.comment}</div>}
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <p className="expense-muted">No approval history yet.</p>
            )}
          </>
        )}
      </Modal>

      {isTourOpen && (
        <GuidedTour
          steps={tourSteps}
          onClose={() => setIsTourOpen(false)}
          storageKey={TOUR_STORAGE_KEY}
        />
      )}
      {!isTourOpen && isTourHintOpen && (
        <GuidedTour
          steps={hintSteps}
          onClose={() => setIsTourHintOpen(false)}
          storageKey={TOUR_HINT_STORAGE_KEY}
        />
      )}
    </Card>
  );
};

export default ExpenseApproval;
